/**
 * DecisionPeek — the side detail panel (UX-Inventar T5 / "Details öffnen seitlich, nicht als Pop-up").
 * Header (title + progress + prev/next/close) → body (facts/PDF/history) → footer (DecisionBar).
 * Keyboard J/K/E/Esc are wired by the host screen.
 */
import type { ReactNode } from 'react';
import { ChevronDown, ChevronUp, X } from 'lucide-react';
import { cn } from '../../lib/cn';

interface DecisionPeekProps {
  title: string;
  subtitle?: string;
  progress?: string;
  onClose: () => void;
  onPrev?: () => void;
  onNext?: () => void;
  footer?: ReactNode;
  /**
   * Panel width utility (default `w-[440px]` — unchanged for every existing
   * consumer). Wider queue flows (e.g. the Rechnungskontrolle peek) pass a larger
   * class (`w-[672px]`) per the prototype sheet. [Story UI-U4]
   */
  width?: string;
  children: ReactNode;
}

export function DecisionPeek({ title, subtitle, progress, onClose, onPrev, onNext, footer, width = 'w-[440px]', children }: DecisionPeekProps) {
  return (
    <div className={cn('fixed inset-y-0 right-0 top-12 z-30 flex max-w-[88vw] flex-col border-l border-border bg-card shadow-xl', width)}>
      <div className="flex items-center gap-2 border-b border-border px-4 py-3">
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-semibold">{title}</div>
          {subtitle && <div className="truncate text-xs text-muted-foreground">{subtitle}</div>}
        </div>
        {progress && <span className="text-xs text-muted-foreground [font-variant-numeric:tabular-nums]">{progress}</span>}
        <div className="flex items-center">
          <button onClick={onPrev} className="grid size-7 place-items-center rounded hover:bg-accent" title="Vorheriger (K)">
            <ChevronUp className="size-4" />
          </button>
          <button onClick={onNext} className="grid size-7 place-items-center rounded hover:bg-accent" title="Nächster (J)">
            <ChevronDown className="size-4" />
          </button>
          <button onClick={onClose} className="ml-1 grid size-7 place-items-center rounded hover:bg-accent" title="Schliessen (Esc)">
            <X className="size-4" />
          </button>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto p-4">{children}</div>
      {footer && <div className="border-t border-border p-3">{footer}</div>}
    </div>
  );
}
